/**
 * Global Functionality
 * The logic in this is intended to ONLY contain any functionality
 * that is required on every page load.
 *
 * NOTE: Please do not include any page-specific logic here.
 **/

import Logger from "../lib/logger";
import ThemeBannerHero from "../partials/ThemeBannerHero";
import ThemeCarouselBanner from "../partials/ThemeCarouselBanner";
import ProfessionalImageCarousel from "../partials/ProfessionalImageCarousel";
import ProfessionalVideoAnimation from "../partials/ProfessionalVideoAnimation";
import ProfessionalSimpleDspSetup from "../partials/ProfessionalSimpleDspSetup";
import ProfessionalMediaTextSoftwareModal from "../partials/ProfessionalMediaTextSoftwareModal";
import ProfessionalSystemCalculator from "../partials/ProfessionalSystemCalculator";
import ThemeMobileNav from "../partials/ThemeMobileNav";
import MegaMenuLoader from "../partials/MegaMenuLoader";
import ThemePopupGalleryGrid from "../partials/ThemePopupGalleryGrid";
import ThemeRevealableElement from "../partials/ThemeRevealableElement";
import "../partials/ThemeScrubber";
import "../partials/ProfessionalBeforeAfter";
import '../partials/ThemeMosaic';
import '../partials/SimpleCarousel';
import "../partials/ThemePopupScrubber";
import ThemeSideOpenAccordion from "../partials/ThemeSideOpenAccordion";
import ThemeVideoBaner from "../partials/ThemeVideoBanner";
import ThemeVideoContentModule from "../partials/ThemeVideoContentModule";
import '../partials/ThemeSearchBar';
import '../partials/ThemeCompareModels';
import '../partials/ThemeProductGrid';
import '../partials/ProductSpecGrid';
import '../partials/DownloadButton';
import '../partials/CopyButton';
import '../partials/ShopifyHostedVideoPlayer';

export default function () {
	const NAME = "Global";
	Logger.info(`${NAME} > loading`);

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	const themeBannerHeroDoms = document.querySelectorAll(
		`[data-theme-banner-hero]`
	);
	const themeHeaderDom = document.querySelector(`[data-theme-header]`);
	const professionalImageCarouselDoms = document.querySelectorAll(
		`[data-professional-image-carousel]`
	);
	const professionalVideoAnimationDoms = document.querySelectorAll(
		`[data-professional-video-animation]`
	);
	const professionalSimpleDspSetupDoms = document.querySelectorAll(
		`[data-professional-simple-dsp-setup]`
	);
	const professionalMediaTextDoms = document.querySelectorAll(
		`[data-professional-media-text]`
	);
	const professionalSystemCalculatorDoms = document.querySelectorAll(
		`[data-professional-system-calculator]`
	);
	const themePopupGalleryGridDoms = document.querySelectorAll(
		`[data-theme-popup-gallery-grid]`
	);
	const themeRevealableElementDoms = document.querySelectorAll(
		`[data-theme-revealable-element]`
	);
	const themeScrubberBannerDoms = document.querySelectorAll(
		`[data-theme-scrubber-banner]`
	);
	const themeSideOpenAccordionDoms = document.querySelectorAll(
		`[data-theme-side-open-accordion]`
	);
	const themeVideoBannerDoms = document.querySelectorAll(
		`[data-theme-video-banner]`
	);
	const themeVideoContentModules = document.querySelectorAll(
		`[data-theme-video-content-module]`
	);

	// Logic
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * init --- Initialization block
	 */
	// Lazy-loads mega-menu levels 2-4 via the Section Rendering API.
	// Shared with ThemeMobileNav so mobile taps can trigger fetches too.
	let megaMenuLoader = null;

	function init() {
		Logger.info(`${NAME} > init`);
		initMegaMenuLoader();
		initThemeMobileNav();
		initThemeBannerHeros();
		initThemeCarouselBanners();
		initProfessionalImageCarousels();
		initProfessionalVideoAnimations();
		initProfessionalSimpleDspSetups();
		initProfessionalMediaTextSoftwareModals();
		initProfessionalSystemCalculators();
		initThemePopupGalleryGridDoms();
		initRevealableElements();
		/* initThemeScrubberBanners(); */
		initThemeSideOpenAccordions();
		initThemeVideoBanners();
		initThemeVideoContentModules();
	}

	/**
	 * initThemeBannerHeros - Initialize all ThemeBannerHeros
	 */
	function initThemeBannerHeros() {
		Logger.info(`${NAME} > initThemeBannerHeros`);

		themeBannerHeroDoms.forEach((v) => {
			const el = new ThemeBannerHero(v);
			el.init();
		});
	}

	/**
	 * initThemeCarouselBanners - Initialize and ThemeCarouselBanners
	 */
	function initThemeCarouselBanners() {
		Logger.info(`${NAME} > initThemeCarouselBanners`);

		document.querySelectorAll(`[data-theme-carousel-banner]`).forEach(initThemeCarouselBanner);
	}

	function initThemeCarouselBanner(dom) {
		if (!dom || dom.dataset.themeCarouselBannerInitialized === 'true') return;

		dom.dataset.themeCarouselBannerInitialized = 'true';

		const el = new ThemeCarouselBanner(dom);
		el.init();
	}

	/**
	 * initProfessionalImageCarousels - Professional image carousel sections
	 */
	function initProfessionalImageCarousels() {
		Logger.info(`${NAME} > initProfessionalImageCarousels`);

		professionalImageCarouselDoms.forEach((v) => {
			const el = new ProfessionalImageCarousel(v);
			el.init();
		});
	}

	/**
	 * initProfessionalVideoAnimations — scrubbable video + timeline sections
	 */
	function initProfessionalVideoAnimations() {
		Logger.info(`${NAME} > initProfessionalVideoAnimations`);

		professionalVideoAnimationDoms.forEach((v) => {
			const el = new ProfessionalVideoAnimation(v);
			el.init();
		});
	}

	/**
	 * initProfessionalSimpleDspSetups — banded media + Blaze iframe modal
	 */
	function initProfessionalSimpleDspSetups() {
		Logger.info(`${NAME} > initProfessionalSimpleDspSetups`);

		professionalSimpleDspSetupDoms.forEach((v) => {
			const el = new ProfessionalSimpleDspSetup(v);
			el.init();
		});
	}

	/**
	 * initProfessionalMediaTextSoftwareModals — hash-addressable iframe modals
	 * configured by Media & Text Software modal blocks.
	 */
	function initProfessionalMediaTextSoftwareModals() {
		Logger.info(`${NAME} > initProfessionalMediaTextSoftwareModals`);

		professionalMediaTextDoms.forEach((dom) => {
			const modal = new ProfessionalMediaTextSoftwareModal(dom);
			modal.init();
		});
	}

	/**
	 * initProfessionalSystemCalculators — auto-resizing ssc.sonance.com iframe
	 */
	function initProfessionalSystemCalculators() {
		Logger.info(`${NAME} > initProfessionalSystemCalculators`);

		professionalSystemCalculatorDoms.forEach((v) => {
			const el = new ProfessionalSystemCalculator(v);
			el.init();
		});
	}

	/**
	 * initMegaMenuLoader - Initialize the mega-menu lazy loader
	 */
	function initMegaMenuLoader() {
		Logger.info(`${NAME} > initMegaMenuLoader`);

		if (!themeHeaderDom) return;

		megaMenuLoader = new MegaMenuLoader(themeHeaderDom);
		megaMenuLoader.init();
	}

	/**
	 * initThemeMobileNav - Initialize the mobile nav
	 */
	function initThemeMobileNav() {
		Logger.info(`${NAME} > initThemeMobileNav`);

		if (!themeHeaderDom) return;

		const el = new ThemeMobileNav(themeHeaderDom, megaMenuLoader);
		el.init();
	}

	/**
	 * initThemePopupGalleryGridDoms - Initialize all Popup Grid doms
	 */
	function initThemePopupGalleryGridDoms() {
		Logger.info(`${NAME} > initThemePopupGalleryGridDoms`);

		themePopupGalleryGridDoms.forEach((v) => {
			const el = new ThemePopupGalleryGrid(v);
			el.init();
		});
	}

	/**
	 * initRevealableElements - Initialize all revealable elements
	 */
	function initRevealableElements() {
		Logger.info(`${NAME} > initRevealableElements`);
		themeRevealableElementDoms.forEach((dom) => {
			const el = new ThemeRevealableElement(dom);
			el.init();
		});
	}

	/**
	 * initThemeScrubberBanners - Initialize all scrubber banners
	 */
	/* function initThemeScrubberBanners() {
		Logger.info(`${NAME} > initThemeScrubberBanners`);
		themeScrubberBannerDoms.forEach((dom) => {
			const el = new ThemeScrubberBanner(dom);
			el.init();
		});
	} */

	/**
	 * initThemeSideOpenAccordions - Initialize all side open accordions
	 */
	function initThemeSideOpenAccordions() {
		Logger.info(`${NAME} > initThemeSideOpenAccordions`);
		themeSideOpenAccordionDoms.forEach((dom) => {
			const el = new ThemeSideOpenAccordion(dom);
			el.init();
		});
	}

	/**
	 * initThemeVideoBanners - Initialize all video banners
	 */
	function initThemeVideoBanners() {
		Logger.info(`${NAME} > initThemeVideoBanners`);

		themeVideoBannerDoms.forEach((v) => {
			const el = new ThemeVideoBaner(v);
			el.init();
		});
	}

	/**
	 * initThemeVideoContentModules --- Initialize any ThemeVideoContentModules
	 */
	function initThemeVideoContentModules() {
		Logger.info(`${NAME} > initThemeVideoContentModules`);

		themeVideoContentModules.forEach((v) => {
			const el = new ThemeVideoContentModule(v);
			el.init();
		});
	}

	// Do not edit below this line...
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	Logger.info(`${NAME} > loaded`);

	init();

	document.addEventListener('shopify:section:load', (e) => {
		const dom = e.target.querySelector(`[data-theme-carousel-banner]`);
		if (!dom) return;

		Logger.info(`${NAME} > shopify:section:load > re-initializing carousel banner`);
		initThemeCarouselBanner(dom);
	});
}
